import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ComposableMap, Geographies, Geography, Marker, Line, ZoomableGroup } from "react-simple-maps";
import ThreatGlobe from "@/pages/ThreatGlobe";
import GlobeErrorBoundary from "@/pages/GlobeErrorBoundary";
import CVETicker from "@/pages/kiosk/CVETicker";
import { API_BASE, kioskApi } from "@/lib/api";
import { hasWebGLSupport } from "@/lib/webgl";
import { ShieldAlert, Globe } from "lucide-react";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const SEVERITY_STYLES = {
  low:      { color: "#00F0FF", label: "LOW" },
  medium:   { color: "#FFC542", label: "MED" },
  high:     { color: "#FF6B35", label: "HIGH" },
  critical: { color: "#FF2E63", label: "CRIT" },
};

const MAX_EVENTS = 60;

/**
 * Kiosk Threat Map — unauthenticated, token-gated, read-only wall display.
 * URL: /kiosk/threat-map?token=<raw>
 *
 * Rendered outside the main Layout so there's no sidebar / no login gate.
 * Always in globe + wall-mode style. No filters, no drag, no interaction.
 */
export default function KioskThreatMap() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [events, setEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState(null);
  const [presetName, setPresetName] = useState("");
  const [forcedFlat, setForcedFlat] = useState(false);
  const webglSupported = useMemo(() => hasWebGLSupport(), []);

  // Load preset first — so the initial paint respects the wall's config.
  useEffect(() => {
    if (!token) { setError("Missing kiosk token in URL."); return; }
    (async () => {
      try {
        const r = await kioskApi.get(`/kiosk/threat-map/preset?token=${encodeURIComponent(token)}`);
        setPreset(r.data.preset || null);
        setPresetName(r.data.name || "");
      } catch (e) {
        if (e.response?.status === 401) {
          setError("Kiosk token is invalid, revoked, or expired.");
        }
      }
    })();
  }, [token]);

  // Seed with snapshot
  useEffect(() => {
    if (!token) { setError("Missing kiosk token in URL."); return; }
    let ignore = false;
    (async () => {
      try {
        const r = await kioskApi.get(`/kiosk/threat-map/snapshot?count=30&token=${encodeURIComponent(token)}`);
        if (ignore) return;
        setEvents(r.data.events.slice(-MAX_EVENTS));
      } catch (e) {
        setError(e.response?.status === 401
          ? "Kiosk token is invalid, revoked, or expired."
          : "Failed to load initial data.");
      }
    })();
    return () => { ignore = true; };
  }, [token]);

  // Live SSE via fetch (token in URL query)
  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    (async () => {
      try {
        const resp = await fetch(
          `${API_BASE}/kiosk/threat-map/stream?token=${encodeURIComponent(token)}`,
          { signal: controller.signal },
        );
        if (!resp.ok || !resp.body) {
          setError("Failed to open live stream (token may be invalid).");
          return;
        }
        setConnected(true);
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const chunks = buf.split("\n\n");
          buf = chunks.pop() ?? "";
          for (const chunk of chunks) {
            const lines = chunk.split("\n");
            const isEv = lines.some((l) => l.startsWith("event:"));
            const dl = lines.find((l) => l.startsWith("data:"));
            if (!dl) continue;
            const payload = dl.slice(5).trim();
            if (!payload || isEv) continue;
            try {
              const ev = JSON.parse(payload);
              if (!ev.source || !ev.target) continue;
              setEvents((prev) => {
                const next = [...prev, ev];
                return next.length > MAX_EVENTS ? next.slice(-MAX_EVENTS) : next;
              });
            } catch { /* ignore */ }
          }
        }
      } catch { /* aborted or disconnected */ }
      finally { setConnected(false); }
    })();
    return () => controller.abort();
  }, [token]);

  // Presence heartbeat — pings backend every 20s so the admin sees this wall
  // as "online" even during long-lived SSE streams. Fires immediately, then on interval.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const ping = () => {
      if (cancelled) return;
      kioskApi.post(`/kiosk/heartbeat?token=${encodeURIComponent(token)}`).catch(() => {});
    };
    ping();
    const t = setInterval(ping, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [token]);

  // Apply preset severity filter.
  const filteredEvents = useMemo(() => {
    const f = preset?.severity_filter;
    if (!f) return events;
    return events.filter((e) => f[e.severity] !== false);
  }, [events, preset]);

  const stats = useMemo(() => {
    const c = { low: 0, medium: 0, high: 0, critical: 0, real: 0 };
    for (const e of filteredEvents) { c[e.severity] += 1; if (e.real) c.real += 1; }
    return c;
  }, [filteredEvents]);

  const liveHotspots = useMemo(() => {
    const agg = new Map();
    for (const e of filteredEvents) {
      const key = `${e.source.city}|${e.source.country}`;
      const cur = agg.get(key) || { ...e.source, count: 0, maxSev: 0 };
      cur.count += 1;
      cur.maxSev = Math.max(cur.maxSev, e.severity_weight || 1);
      agg.set(key, cur);
    }
    return [...agg.values()].sort((a, b) => b.count - a.count);
  }, [filteredEvents]);

  if (error) {
    return (
      <div className="h-screen w-screen bg-[#060606] text-white flex items-center justify-center p-8" data-testid="kiosk-error">
        <div className="max-w-md text-center">
          <ShieldAlert className="w-12 h-12 text-[#FF2E63] mx-auto mb-4" />
          <h1 className="font-display font-black text-2xl mb-2">Access Denied</h1>
          <p className="text-[#8E9196] text-sm font-mono">{error}</p>
          <p className="text-[#52525B] text-xs font-mono uppercase tracking-widest mt-6">
            ForensiQ Kiosk · Ask an admin for a fresh URL
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-[#060606] text-white overflow-hidden relative" data-testid="kiosk-threat-map">
      {/* Top HUD */}
      <div className="absolute top-4 left-6 right-6 z-10 flex items-start justify-between gap-6 pointer-events-none">
        <div>
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.75} />
            <h1 className="font-display font-black text-2xl tracking-tight" data-testid="kiosk-title">
              {preset?.title_override || presetName ? (preset?.title_override || presetName).toUpperCase() : "THREAT MAP · KIOSK"}
            </h1>
            <div className="flex items-center gap-1.5 px-2 py-0.5 border border-white/10 rounded-sm bg-black/40">
              <span
                className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-[#00F0FF]" : "bg-[#8E9196]"}`}
                style={connected ? { boxShadow: "0 0 8px #00F0FF" } : undefined}
              />
              <span className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196]">
                {connected ? "LIVE · READ-ONLY" : "CONNECTING"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          {Object.keys(SEVERITY_STYLES).map((k) => (
            <div key={k} className="flex flex-col items-center min-w-[92px] px-4 py-2 rounded-sm bg-black/40 backdrop-blur border border-white/10">
              <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: SEVERITY_STYLES[k].color }}>
                {SEVERITY_STYLES[k].label}
              </span>
              <span className="font-display font-black text-3xl leading-none mt-1"
                    style={{ color: SEVERITY_STYLES[k].color, textShadow: `0 0 12px ${SEVERITY_STYLES[k].color}66` }}>
                {stats[k]}
              </span>
            </div>
          ))}
          <div className="flex flex-col items-center min-w-[92px] px-4 py-2 rounded-sm bg-black/40 backdrop-blur border border-[#FF2E63]/40">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#FF2E63]">REAL IOC</span>
            <span className="font-display font-black text-3xl leading-none mt-1 text-[#FF2E63]"
                  style={{ textShadow: "0 0 14px #FF2E6366" }}>{stats.real}</span>
          </div>
        </div>
      </div>

      {/* Globe or 2D map, per preset (falls back to 2D if WebGL is unavailable) */}
      <div className="absolute inset-0">
        {(preset?.view === "map" || !webglSupported || forcedFlat) ? (
          <KioskFlatMap events={filteredEvents} hotspots={liveHotspots} />
        ) : (
          <GlobeErrorBoundary onFallback={() => setForcedFlat(true)}>
            <ThreatGlobe
              events={filteredEvents}
              autoRotate={preset?.auto_rotate !== false}
              wallMode={true}
            />
          </GlobeErrorBoundary>
        )}
      </div>

      {/* Top-right: hotspot panel */}
      {(preset?.show_hotspot_panel !== false) && (
        <div className="absolute bottom-24 right-6 z-10 min-w-[240px] max-w-[280px] px-4 py-3 rounded-sm bg-black/50 backdrop-blur border border-white/10" data-testid="kiosk-hotspot-panel">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">
            Top Origin Hotspots
          </div>
          {liveHotspots.slice(0, 6).map((h) => (
            <div key={`${h.city}-${h.country}`} className="flex items-center gap-2 text-sm">
              <span className="text-white/90 truncate flex-1">
                {h.city} <span className="text-[#52525B]">/{h.country}</span>
              </span>
              <span className="font-mono text-xs"
                    style={{ color: h.maxSev >= 4 ? "#FF2E63" : h.maxSev >= 3 ? "#FF6B35" : h.maxSev >= 2 ? "#FFC542" : "#00F0FF" }}>
                {h.count}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Bottom CVE ticker (per preset) */}
      {(preset?.show_cve_ticker !== false) && (
        <div className="absolute bottom-0 inset-x-0 z-10">
          <CVETicker kioskToken={token} />
        </div>
      )}

      {/* Footer credit */}
      <div className="absolute bottom-14 left-6 z-10 text-[9px] font-mono uppercase tracking-widest text-[#52525B]">
        ForensiQ · Read-only Kiosk · <span className="text-[#00F0FF]">Ahamed Fasudeen</span>
      </div>
    </div>
  );
}

// Simple flat map view used when preset.view === "map"
function KioskFlatMap({ events, hotspots }) {
  const recentArcs = events.slice(-24);
  return (
    <div className="w-full h-full relative">
      <ComposableMap projection="geoEqualEarth" projectionConfig={{ scale: 200 }}
                     style={{ width: "100%", height: "100%", background: "#060606" }}>
        <ZoomableGroup center={[10, 20]} zoom={1}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) => geographies.map((geo) => (
              <Geography key={geo.rsmKey} geography={geo}
                fill="#0f1922" stroke="#1a2833" strokeWidth={0.5}
                style={{ default: { outline: "none" }, hover: { outline: "none" }, pressed: { outline: "none" } }} />
            ))}
          </Geographies>
          {recentArcs.map((e) => {
            const c = e.severity === "critical" ? "#FF2E63"
                    : e.severity === "high"     ? "#FF6B35"
                    : e.severity === "medium"   ? "#FFC542" : "#00F0FF";
            return (
              <Line key={e.id}
                    from={[e.source.lon, e.source.lat]}
                    to={[e.target.lon, e.target.lat]}
                    stroke={c} strokeWidth={e.severity_weight >= 3 ? 1.2 : 0.7}
                    strokeOpacity={0.6} strokeLinecap="round"
                    style={{ filter: `drop-shadow(0 0 3px ${c})` }} />
            );
          })}
          {hotspots.slice(0, 20).map((h) => {
            const c = h.maxSev >= 4 ? "#FF2E63" : h.maxSev >= 3 ? "#FF6B35" : h.maxSev >= 2 ? "#FFC542" : "#00F0FF";
            const r = 3 + Math.min(8, h.count * 0.6);
            return (
              <Marker key={`${h.city}-${h.country}`} coordinates={[h.lon, h.lat]}>
                <circle r={r + 4} fill={c} fillOpacity={0.15} />
                <circle r={r} fill={c} fillOpacity={0.9}
                        style={{ filter: `drop-shadow(0 0 6px ${c})` }} />
                <circle r={r * 0.4} fill="#060606" />
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>
    </div>
  );
}
